import { destRegionOf } from "./inboxMintMemory";

/** Requester region for an ICAO. Home base only biases the pick; it does not choose the route. */
export type PartyRegion =
  | "maritimes"
  | "quebec"
  | "ontario"
  | "prairies"
  | "west"
  | "us_ne"
  | "us_mid"
  | "us_fl"
  | "us_other"
  | "caribbean"
  | "mexico"
  | "south_america"
  | "canada"
  | "europe"
  | "other";

const FLORIDA = new Set([
  "KTPA",
  "KRSW",
  "KSRQ",
  "KPIE",
  "KDAB",
  "KJAX",
  "KTLH",
  "KPNS",
  "KECP",
  "KSFB",
  "KORL",
  "KTMB",
  "KEYW",
]);

type Pack = {
  first: readonly string[];
  last: readonly string[];
  companies: readonly string[];
  domains: readonly string[];
};

const PACKS: Record<PartyRegion, Pack> = {
  us_fl: {
    first: ["Elena", "James", "Marisol", "Andrew", "Camille", "Robert"],
    last: ["Callahan", "Vasquez", "Bennett", "Herrera", "Dalton", "Perez"],
    companies: ["Bayshore Holdings", "Suncoast Advisory", "Davis Islands Capital", "Palm Harbor Logistics"],
    domains: ["bayshoreholdings.com", "suncoastadvisory.com", "davisislands.com", "palmharborlogistics.com"],
  },
  us_ne: {
    first: ["Margaret", "Daniel", "Priya", "Jonathan", "Claire", "Noah"],
    last: ["Ellison", "Cho", "Brennan", "Kaplan", "Moretti", "Walsh"],
    companies: ["Hudson Yard Partners", "Liberty Street Counsel", "Harbor Light Capital", "Midtown Charter Group"],
    domains: ["hudsonyardpartners.com", "libertystreetcounsel.com", "harborlightcap.com", "midtowncharter.com"],
  },
  us_mid: {
    first: ["Helen", "Marcus", "Alicia", "Grant", "Diane", "Owen"],
    last: ["Whitaker", "Nguyen", "Powell", "Adler", "Brooks", "Shah"],
    companies: ["Potomac Advisory", "Chesapeake Partners", "Piedmont Holdings", "Beltway Medical Group"],
    domains: ["potomacadvisory.com", "chesapeakepartners.com", "piedmontholdings.com", "beltwaymedical.com"],
  },
  us_other: {
    first: ["Lauren", "Chris", "Anita", "Paul", "Megan", "Eric"],
    last: ["Harper", "Nguyen", "Coleman", "Patel", "Brooks", "Diaz"],
    companies: ["Meridian Capital", "Redwood Range Partners", "Lakeshore Holdings", "Summit Field Group"],
    domains: ["meridiancap.com", "redwoodrange.com", "lakeshoreholdings.com", "summitfield.com"],
  },
  maritimes: {
    first: ["James", "Margaret", "Colin", "Siobhan", "Andrew", "Catherine"],
    last: ["MacNeil", "Campbell", "Beaton", "Fraser", "MacDonald", "MacLeod"],
    companies: ["Cabot Links", "Halifax Capital", "Fundy Partners", "North Shore Estates"],
    domains: ["cabotlinks.com", "halifaxcapital.com", "fundypartners.com", "northshoreestates.com"],
  },
  quebec: {
    first: ["Camille", "Marc", "Sophie", "Etienne", "Chloe", "Luc"],
    last: ["Tremblay", "Gagnon", "Roy", "Cote", "Bouchard", "Gauthier"],
    companies: ["Vieux-Port Conseil", "Laurentian Holdings", "Mont-Royal Partners", "Saint-Laurent Aero"],
    domains: ["vieuxportconseil.com", "laurentianholdings.com", "montroyalpartners.com", "stlaurentaero.com"],
  },
  ontario: {
    first: ["Amrita", "David", "Natalie", "Omar", "Claire", "Ben"],
    last: ["Singh", "Campbell", "Leung", "Haddad", "Murphy", "Kowalski"],
    companies: ["Bay Street Advisory", "Harbourfront Holdings", "Queen's Quay Partners", "Lakehead Capital"],
    domains: ["baystreetadvisory.com", "harbourfrontholdings.com", "queensquay.com", "lakeheadcapital.com"],
  },
  prairies: {
    first: ["Hannah", "Eric", "Megan", "Josh", "Taylor", "Amy"],
    last: ["Nguyen", "Friesen", "Olson", "Desjarlais", "Berg", "Singh"],
    companies: ["Prairie Grain Partners", "Bow Valley Holdings", "South Saskatchewan Capital", "Chinook Advisory"],
    domains: ["prairiegrain.com", "bowvalleyholdings.com", "southsaskcapital.com", "chinookadvisory.com"],
  },
  west: {
    first: ["Ava", "Liam", "Nora", "Ethan", "Sonia", "Chris"],
    last: ["Chen", "MacKay", "Santos", "Bergstrom", "Gill", "Andersen"],
    companies: ["Coal Harbour Partners", "Fraser Valley Holdings", "Island Range Advisory", "Pacific Rim Capital"],
    domains: ["coalharbourpartners.com", "fraservalleyholdings.com", "islandrange.com", "pacificrimcap.com"],
  },
  canada: {
    first: ["Claire", "Marc", "Aisha", "Owen", "Leah", "Simon"],
    last: ["Roy", "Campbell", "Singh", "MacLeod", "Tremblay", "Nguyen"],
    companies: ["Northern Range Holdings", "Shield Advisory", "Trans-Canada Partners", "Boreal Capital"],
    domains: ["northernrange.com", "shieldadvisory.com", "transcanadapartners.com", "borealcap.com"],
  },
  caribbean: {
    first: ["Camille", "Andre", "Isla", "Mateo", "Anya", "Luis"],
    last: ["Clarke", "Baptiste", "Knowles", "Rivera", "Joseph", "Alleyne"],
    companies: ["Nassau Waterfront Holdings", "Harbour Island Partners", "Castries Advisory", "Bridgetown Trade"],
    domains: ["nassauwaterfront.com", "harbourislandpartners.com", "castriesadvisory.com", "bridgetowntrade.com"],
  },
  mexico: {
    first: ["Lucia", "Mateo", "Sofia", "Diego", "Valeria", "Javier"],
    last: ["Hernandez", "Garcia", "Ramirez", "Lopez", "Castillo", "Morales"],
    companies: ["Costa Maya Holdings", "Polanco Advisory", "Baja Sur Partners", "Yucatan Trade Group"],
    domains: ["costamayholdings.com", "polancoadvisory.com", "bajasurpartners.com", "yucatatrade.com"],
  },
  south_america: {
    first: ["Camila", "Mateo", "Valentina", "Santiago", "Lucia", "Andres"],
    last: ["Ferreira", "Alvarez", "Silva", "Rojas", "Costa", "Mendes"],
    companies: ["Andes Charter Group", "Rio Branco Holdings", "Recoleta Advisory", "Pacifico Sur Partners"],
    domains: ["andeschartergroup.com", "riobrancoholdings.com", "recoletaadvisory.com", "pacificosur.com"],
  },
  europe: {
    first: ["Charlotte", "Henrik", "Amelie", "Luca", "Ingrid", "Owen"],
    last: ["Berg", "Moreau", "Rossi", "Keller", "Walsh", "Novak"],
    companies: ["Thames Reach Partners", "Canal Saint-Martin Conseil", "Alster Holdings", "Tiber Advisory"],
    domains: ["thamesreach.com", "canalsaintmartin.com", "alsterholdings.com", "tiberadvisory.com"],
  },
  other: {
    first: ["Alex", "Jordan", "Riley", "Sam", "Quinn", "Avery"],
    last: ["Parker", "Nguyen", "Brooks", "Patel", "Keller", "Diaz"],
    companies: ["North Range Holdings", "Fieldlight Advisory", "Meridian Desk Partners", "Harbor and Range"],
    domains: ["northrangeholdings.com", "fieldlightadvisory.com", "meridiandesk.com", "harborandrange.com"],
  },
};

function roll(rng: () => number): number {
  const n = rng();
  return Number.isFinite(n) ? Math.min(0.999999, Math.max(0, n)) : 0;
}

function pick<T>(items: readonly T[], rng: () => number): T {
  return items[Math.floor(roll(rng) * items.length)] ?? items[0]!;
}

export function partyRegionForIcao(icao?: string | null): PartyRegion {
  const code = (icao ?? "").trim().toUpperCase();
  if (!code) return "other";
  if (FLORIDA.has(code)) return "us_fl";
  const known = destRegionOf(code);
  if (known !== "other" && known !== "us_other") return known;
  if (code.startsWith("SA") || code.startsWith("SB") || code.startsWith("SC") || code.startsWith("SK") || code.startsWith("SP") || code.startsWith("SU") || code.startsWith("SG")) {
    return "south_america";
  }
  if (code.startsWith("MM")) return "mexico";
  if (/^(MY|TJ|TI|TN|MK|MW|MB|TX)/.test(code)) return "caribbean";
  if (code.startsWith("CY") || code.startsWith("CZ")) return "canada";
  if (/^(EG|LF|ED|EH|EI|LE|EB|EK|EN|ES|LS|LI|LP|LG)/.test(code)) return "europe";
  if (code.startsWith("K")) return "us_other";
  return "other";
}

export function pickPartyRegion(originIcao: string, destIcao: string, homeIcao: string | undefined, rng: () => number): PartyRegion {
  const origin = partyRegionForIcao(originIcao);
  const dest = partyRegionForIcao(destIcao);
  const home = partyRegionForIcao(homeIcao);
  const n = roll(rng);
  if (n < 0.5) return origin;
  if (n < 0.8) return dest;
  return home;
}

function slug(value: string): string {
  return value
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[^a-z]+/g, ".")
    .replace(/^\.+|\.+$/g, "");
}

export type RegionalParty = {
  clientName: string;
  clientCompany: string;
  clientEmail: string;
  region: PartyRegion;
  kind: "private" | "company";
};

export function regionalParty(input: {
  originIcao: string;
  destIcao: string;
  homeIcao?: string;
  operatorName?: string;
  rng?: () => number;
}): RegionalParty {
  const rng = input.rng ?? Math.random;
  const region = pickPartyRegion(input.originIcao, input.destIcao, input.homeIcao, rng);
  const pack = PACKS[region];
  const first = pick(pack.first, rng);
  const last = pick(pack.last, rng);
  const privateClient = roll(rng) < 0.4;
  const operator = (input.operatorName ?? "").trim().toLowerCase();
  let firm = privateClient ? `${last} Family Office` : pick(pack.companies, rng);
  if (operator && firm.trim().toLowerCase() === operator) {
    firm = pack.companies.find((name) => name.trim().toLowerCase() !== operator) ?? `${last} Family Office`;
  }
  const domain = pick(pack.domains, rng);
  const local = slug(`${first}.${last}`) || "desk.client";
  return {
    clientName: `${first} ${last}`,
    clientCompany: firm,
    clientEmail: `${local}@${domain}`,
    region,
    kind: privateClient ? "private" : "company",
  };
}

/** Keep a model-written name when it is real, and replace the operator or a blank. */
export function settleParty(
  drafted: { clientName?: string; clientCompany?: string; clientEmail?: string },
  route: { originIcao: string; destIcao: string; homeIcao?: string; operatorName?: string; rng?: () => number },
): RegionalParty {
  const fresh = regionalParty(route);
  const operator = (route.operatorName ?? "").trim().toLowerCase();
  const name = (drafted.clientName ?? "").trim();
  const firm = (drafted.clientCompany ?? "").trim();
  const email = (drafted.clientEmail ?? "").trim();
  const firmOk = firm && firm.toLowerCase() !== operator;
  const emailOk = email.includes("@") && !email.toLowerCase().endsWith("@company.local") && email !== "desk@local";
  return {
    ...fresh,
    clientName: name || fresh.clientName,
    clientCompany: firmOk ? firm : fresh.clientCompany,
    clientEmail: emailOk ? email : fresh.clientEmail,
  };
}
